import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  Space, 
  message,
  Tag,
  Typography,
  Badge,
  Row,
  Col,
  Modal,
  Select,
  Dropdown,
  Drawer
} from 'antd';
import { 
  SearchOutlined, 
  PhoneOutlined,
  FilterOutlined,
  ExclamationCircleOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  MoreOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faDownload,
  faTableCells,
  faList,
  faBoxArchive,
  faVial,
  faEnvelope,
  faUserPen,
  faFileMedical,
  faPhoneVolume,
  faUserPlus,
} from '@fortawesome/free-solid-svg-icons';
// AdminLayout now provided at router level
import PatientService from '../../services/PatientService';
import UserManagementService from '../../services/UserManagementService';
import caseService from '../../services/CaseService';
import DataImportService from '../../services/DataImportService';
import { COLORS } from '../../constants/colors';
import { usePageHeader } from '../../layouts/AdminLayout';
import {
  PageContainer,
  ActionButton,
  FilterContainer,
  StyledTable,
  StyledTableContainer,
  RiskTag,
  StyledInput,
  GlobalSelectStyles,
  MobileFilterButton,
  MobileIconButton,
  ActionsSheetList,
  DesktopMembersView,
  MobileMembersList,
  MemberCard,
  MobileMembersPagination
} from './styled';
import HeartLoader from '../../components/shared/HeartLoader';
// removed unused getBPCategory utility import
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import ROUTES from '../../constants/routes';
import FilterDrawer from './components/FilterDrawer';
import BulkExportModal from './components/BulkExportModal';
import SpreadsheetView from './components/SpreadsheetView';
import { getSpreadsheetEgfr } from './utils/spreadsheetEgfr';
import BulkKitActivationModal from './components/BulkKitActivationModal';
import BulkKitResultsModal from './components/BulkKitResultsModal';
import AddMemberModal from './components/AddMemberModal';
import KitStatusIcons, { kitStatusFromPatient } from './components/KitStatusIcons';
import ComposeOutreachModal from '../CaseManagement/components/ComposeOutreachModal';

// Key used to persist members page filters across navigation so users can
// pick up where they left off after viewing a member record.
const MEMBERS_FILTERS_STORAGE_KEY = 'bha.membersPage.filters.v1';

const loadSavedMembersFilters = () => {
  try {
    const raw = sessionStorage.getItem(MEMBERS_FILTERS_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const formatHistoryTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

const { Text } = Typography;

const ScreeningData = ({ forcedDataSource = null, pageTitleOverride = null } = {}) => {
  // Restore previously-saved filters (from sessionStorage) so users return to
  // the same view after clicking into a member record and navigating back.
  const savedFilters = useMemo(() => loadSavedMembersFilters() || {}, []);

  const [screenings, setScreenings] = useState([]);
  const [loading, setLoading] = useState(true); // Start with loading true so table shows spinner immediately
  // Detail modal and inline AI generation UI removed from this page (navigation opens full edit page)
  const [searchText, setSearchText] = useState(savedFilters.searchText || '');
  const [riskFilter, setRiskFilter] = useState(savedFilters.riskFilter || 'all');
  const [dateRange, setDateRange] = useState(() => {
    if (Array.isArray(savedFilters.dateRange) && savedFilters.dateRange.length === 2) {
      const start = dayjs(savedFilters.dateRange[0]);
      const end = dayjs(savedFilters.dateRange[1]);
      if (start.isValid() && end.isValid()) return [start, end];
    }
    return null;
  });
  const [locationFilter, setLocationFilter] = useState(savedFilters.locationFilter || 'all');
  // Dedicated birthday filter (exact date). Sent to the backend as ?dob=YYYY-MM-DD
  // and matched via the indexed dobHash — no decryption of the collection.
  const [dobFilter, setDobFilter] = useState(() => {
    const saved = savedFilters.dobFilter ? dayjs(savedFilters.dobFilter) : null;
    return saved && saved.isValid() ? saved : null;
  });
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter || 'all'); // all, completed, archived, in_progress, pending_review
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isActionsSheetOpen, setIsActionsSheetOpen] = useState(false);
  // removed risk modal state (not used on this page)
  const [patientStats, setPatientStats] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  // Cache of selected member records keyed by id. This preserves the full record
  // data for members that are selected but no longer present in the current
  // (searched/filtered) result set, so a selection isn't lost when the user
  // searches or filters for another member.
  const [selectedRecordsMap, setSelectedRecordsMap] = useState({});
  const [isBulkExportOpen, setIsBulkExportOpen] = useState(false);
  const [isKitActivationOpen, setIsKitActivationOpen] = useState(false);
  const [isKitResultsOpen, setIsKitResultsOpen] = useState(false);
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const [isBulkAssignOpen, setIsBulkAssignOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [bulkAssignHandler, setBulkAssignHandler] = useState(null);
  const [bulkAssignLoading, setBulkAssignLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allEventTitles, setAllEventTitles] = useState([]);
  const [viewMode, setViewMode] = useState(savedFilters.viewMode || 'list'); // 'list' | 'spreadsheet'
  const [dataSourceFilter, setDataSourceFilter] = useState(forcedDataSource || savedFilters.dataSourceFilter || 'all');
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [allMatchingPatientIds, setAllMatchingPatientIds] = useState([]);
  const [pagination, setPagination] = useState({
    current: savedFilters.page || 1,
    pageSize: savedFilters.pageSize || 50,
    total: 0
  });
  const { setPageHeader } = usePageHeader();
  const location = useLocation(); // Track navigation to refresh data when returning to this page

  // Set page header with dynamic count and loading state
  useEffect(() => {
    setPageHeader({
      title: pageTitleOverride || 'Members',
      count: pagination.total || screenings.length,
      countIcon: <FontAwesomeIcon icon={faUsers} />,
      loading: loading,
      loadingType: 'heart'
    });
  }, [setPageHeader, pagination.total, screenings.length, loading, pageTitleOverride]);

  // Debounce ref for search text
  const searchDebounceRef = useRef(null);

  // Track first mount so restored filters don't immediately get reset to page 1
  // by the filter-change effects below.
  const isInitialFilterMount = useRef(true);
  const isInitialSearchMount = useRef(true);

  // Fetch team members for bulk assign
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const [admins, users] = await Promise.all([
          UserManagementService.getUsers({ role: 'admin', limit: 100 }),
          UserManagementService.getUsers({ role: 'user', limit: 100 })
        ]);
        setTeamMembers([...(admins.data || []), ...(users.data || [])]);
      } catch (err) {
        console.error('Failed to load team members:', err);
      }
    };
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    // Load data on mount and when navigating back to this page.
    // location.key changes on each navigation, ensuring data is refreshed
    // while preserving the user's restored page/pageSize from sessionStorage.
    loadScreenings(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Handle filter changes (status, event, date range) - reload from page 1.
  // Skip on the initial mount so restored pagination is honored.
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setPagination(prev => ({ ...prev, current: 1 }));
    setAllMatchingSelected(false);
    setAllMatchingPatientIds([]);
    loadScreenings(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, locationFilter, dateRange, dobFilter, dataSourceFilter, riskFilter]);

  // Debounced search - wait 400ms after user stops typing.
  // Skip on initial mount so restored search text doesn't trigger a reset to page 1.
  useEffect(() => {
    if (isInitialSearchMount.current) {
      isInitialSearchMount.current = false;
      return;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPagination(prev => ({ ...prev, current: 1 }));
      loadScreenings(1, pagination.pageSize);
    }, 400);
    return () => clearTimeout(searchDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Persist filters/pagination so users can pick up where they left off after
  // navigating into a member record and back.
  useEffect(() => {
    try {
      const payload = {
        searchText,
        riskFilter,
        statusFilter,
        locationFilter,
        dataSourceFilter,
        viewMode,
        page: pagination.current,
        pageSize: pagination.pageSize,
        dateRange:
          Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]
            ? [dateRange[0].toISOString(), dateRange[1].toISOString()]
            : null,
        dobFilter: dobFilter && dayjs(dobFilter).isValid() ? dayjs(dobFilter).toISOString() : null,
      };
      sessionStorage.setItem(MEMBERS_FILTERS_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // sessionStorage may be unavailable (e.g., privacy mode); ignore.
    }
  }, [
    searchText,
    riskFilter,
    statusFilter,
    locationFilter,
    dataSourceFilter,
    viewMode,
    dateRange,
    dobFilter,
    pagination.current,
    pagination.pageSize,
  ]);

  const loadScreenings = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    
    try {
      // Get patient records from queue using the new members queue approach
      let statusFilters = [];
      const ALL_QUEUE_STATUSES = [
        'completed',
        'in_progress',
        'pending_review',
        'new_patient',
        'matched',
        'needs_attention',
        'duplicate'
      ];
      if (statusFilter === 'completed') {
        statusFilters = ['completed'];
      } else if (statusFilter === 'archived') {
        statusFilters = ['archived'];
      } else if (statusFilter === 'in_progress') {
        statusFilters = ['in_progress'];
      } else if (statusFilter === 'pending_review') {
        statusFilters = ['pending_review'];
      } else {
        // 'all' - include EVERY known queue status so new / matched patients appear
        statusFilters = ALL_QUEUE_STATUSES;
      }

      // Build filter params to send to backend
      const filterParams = {
        tabFilter: statusFilters,
        page: page,
        limit: pageSize,
        sort: 'newest'
      };

      // Pass search text to backend
      if (searchText && searchText.trim()) {
        filterParams.search = searchText.trim();
      }

      // Pass event title filter to backend
      if (locationFilter && locationFilter !== 'all') {
        filterParams.eventTitle = locationFilter;
      }

      // Pass date range to backend
      if (dateRange && dateRange.length === 2) {
        filterParams.dateFrom = dateRange[0].toISOString();
        filterParams.dateTo = dateRange[1].toISOString();
      }

      // Pass birthday filter to backend (exact date, indexed dobHash lookup)
      if (dobFilter && dayjs(dobFilter).isValid()) {
        filterParams.dob = dayjs(dobFilter).format('YYYY-MM-DD');
      }

      // Pass risk level filter to backend (server-side via denormalized Patient.riskLevel)
      if (riskFilter && riskFilter !== 'all') {
        filterParams.riskLevel = riskFilter;
      }

      // Pass data source filter to backend (forced prop overrides UI selection)
      const effectiveDataSource = forcedDataSource || (dataSourceFilter && dataSourceFilter !== 'all' ? dataSourceFilter : null);
      if (effectiveDataSource) {
        filterParams.dataSource = effectiveDataSource;
      }

      console.log('🔍 [Frontend] loadScreenings filterParams:', JSON.stringify(filterParams, null, 2));

      const response = await PatientService.getAllEventsPatientQueue(filterParams);

      const patients = response.data || [];
      
      // Update pagination state with backend response
      if (response.pagination) {
        setPagination({
          current: response.pagination.page,
          pageSize: response.pagination.limit,
          total: response.pagination.total
        });
      }
      
      // Capture filter options from API response (all event titles across full dataset)
      if (response.filters?.eventTitles) {
        setAllEventTitles(response.filters.eventTitles);
      }
      
      // Capture patient stats from the API response
      if (response.stats) {
        setPatientStats(response.stats);
      }
      
      // Transform patient queue data into screening records format
      // NO LONGER making individual API calls for each patient!
      const screeningRecords = await Promise.all(patients.map(async (patient) => {
        // The patient data is directly in the patient object for the new API response
        const patientData = patient.patient || {
          firstName: patient.firstName,
          lastName: patient.lastName,
          emailAddress: patient.emailAddress,
          phoneNumber: patient.phoneNumber,
          dateOfBirth: patient.dateOfBirth,
          patientId: patient.patientId
        };
        
        // Debug: Log the actual data structure for first few patients
        if (!patientData || !patientData.firstName) return null;

        // Calculate age
        const age = patientData.dateOfBirth 
          ? Math.floor((new Date() - new Date(patientData.dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000))
          : null;

        // Use patient data from the queue response instead of making another API call
        // The backend should populate all necessary data
        const fullPatientData = patientData;

        // Extract vital signs and medical data
        const medicalHistory = fullPatientData?.medicalHistory || {};
        const medications = fullPatientData?.medications || {};
  const screeningData = fullPatientData?.screeningData || {};
  const visits = screeningData.medicalReadings || [];
  const sortedVisits = visits.slice().sort((a,b)=> new Date(b.visitDate)-new Date(a.visitDate));
  const isNum = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
  const BLOOD_FIELDS = ['totalCholesterol','hdlCholesterol','ldlCholesterol','triglycerides','bloodGlucose','a1c','lipoproteinA'];
  // Mirrors backend utils/riskScoreCalculator.js: risk uses only the latest visit with medical testing data
  const visitTestData = (v) => {
    const vitals = (v?.vitalSigns || []).filter(r => r && (isNum(r.systolicBP) || isNum(r.diastolicBP) || isNum(r.heartRate)));
    let blood = null;
    (v?.bloodTests || []).forEach(entry => {
      if (!entry) return;
      [...BLOOD_FIELDS, 'fastingStatus'].forEach(k => {
        if (entry[k] !== null && entry[k] !== undefined) { blood = blood || {}; blood[k] = entry[k]; }
      });
    });
    const ekgs = v?.ekgTests || [];
    return {
      vital: vitals.length ? vitals[vitals.length-1] : null,
      blood,
      ekg: ekgs.length ? ekgs[ekgs.length-1] : null,
      whr: isNum(v?.bodyMeasurements?.whr) ? v.bodyMeasurements.whr : null
    };
  };
  const hasTestData = ({ vital, blood, ekg, whr }) => !!(
    (vital && (isNum(vital.systolicBP) || isNum(vital.diastolicBP) || isNum(vital.heartRate))) ||
    (blood && BLOOD_FIELDS.some(k => isNum(blood[k]))) ||
    (ekg && (ekg.heartRhythm || isNum(ekg.qtcInterval) || typeof ekg.interpretation === 'string')) ||
    isNum(whr)
  );
  const latestVisit = sortedVisits[0];
  const riskVisit = sortedVisits.find(v => hasTestData(visitTestData(v)));
  const latestVisitData = riskVisit ? visitTestData(riskVisit) : null;
  
  // Get data from visit-based structure, with fallback to legacy flat fields
  const latestVital = latestVisitData
    ? latestVisitData.vital
    : (screeningData.systolicBP || screeningData.diastolicBP || screeningData.pulseRate 
        ? { 
            systolicBP: screeningData.systolicBP, 
            diastolicBP: screeningData.diastolicBP, 
            heartRate: screeningData.pulseRate 
          }
        : null);
  
  const latestBlood = latestVisitData
    ? latestVisitData.blood
    : (screeningData.totalCholesterol || screeningData.bloodGlucose 
        ? {
            totalCholesterol: screeningData.totalCholesterol,
            hdlCholesterol: screeningData.hdlCholesterol,
            ldlCholesterol: screeningData.ldlCholesterol,
            triglycerides: screeningData.triglycerides,
            bloodGlucose: screeningData.bloodGlucose,
            a1c: screeningData.a1c,
            lipoproteinA: screeningData.lipoproteinA,
            fastingStatus: screeningData.fastingStatus
          }
        : null);
  
  const latestEkg = latestVisitData
    ? latestVisitData.ekg
    : (screeningData.heartRhythm || screeningData.ekgInterpretation 
        ? {
            heartRhythm: screeningData.heartRhythm,
            qrsDuration: screeningData.qrsDuration,
            prInterval: screeningData.prInterval,
            qtInterval: screeningData.qtInterval,
            qtcInterval: screeningData.qtcInterval,
            interpretation: screeningData.ekgInterpretation
          }
        : null);
        
  const latestWhr = latestVisitData ? latestVisitData.whr : screeningData.whr;
  const hasAnyTestData = hasTestData({ vital: latestVital, blood: latestBlood, ekg: latestEkg, whr: latestWhr });
        
        // Calculate risk level from medical testing data only (no medical history / age)
        let riskLevel = 'unknown';
        let riskScore = 0;
        
        // Vital Signs Risk Factors
        if (latestVital?.systolicBP && latestVital?.diastolicBP) {
          const systolic = parseInt(latestVital.systolicBP);
          const diastolic = parseInt(latestVital.diastolicBP);
          
          if (systolic >= 180 || diastolic >= 110) riskScore += 25; // Stage 2 Hypertension
          else if (systolic >= 140 || diastolic >= 90) riskScore += 15; // Stage 1 Hypertension
          else if (systolic >= 130 || diastolic >= 80) riskScore += 5; // Elevated BP
        }
        
        // WHR (Waist-to-Hip Ratio) Risk Factors - Replaces BMI for body composition assessment
        if (latestWhr) {
          const whr = parseFloat(latestWhr);
          // WHR risk categories based on cardiovascular risk guidelines
          // For men: >1.0 high risk, >0.95 moderate risk
          // For women: >0.85 high risk, >0.80 moderate risk
          // Using general guidelines since we don't have gender in screening data
          if (whr >= 1.0) riskScore += 15; // High cardiovascular risk - equivalent to severely obese BMI
          else if (whr >= 0.90) riskScore += 10; // Moderate cardiovascular risk - equivalent to obese BMI
          else if (whr >= 0.85) riskScore += 5; // Elevated risk - equivalent to overweight BMI
        }
        
        // Cholesterol Risk Factors
        if (latestBlood?.totalCholesterol) {
          const totalChol = parseInt(latestBlood.totalCholesterol);
          if (totalChol >= 240) riskScore += 15; // High
          else if (totalChol >= 200) riskScore += 8; // Borderline high
        }
        
        if (latestBlood?.ldlCholesterol) {
          const ldl = parseInt(latestBlood.ldlCholesterol);
          if (ldl >= 160) riskScore += 12; // High
          else if (ldl >= 130) riskScore += 8; // Borderline high
          else if (ldl >= 100) riskScore += 4; // Above optimal
        }
        
        if (latestBlood?.hdlCholesterol) {
          const hdl = parseInt(latestBlood.hdlCholesterol);
          if (hdl < 40) riskScore += 10; // Low HDL (protective factor missing)
        }
        
        // Blood Glucose Risk Factors
        if (latestBlood?.bloodGlucose) {
          const glucose = parseInt(latestBlood.bloodGlucose);
          if (latestBlood.fastingStatus) {
            // Fasting glucose levels
            if (glucose >= 126) riskScore += 20; // Diabetes
            else if (glucose >= 100) riskScore += 10; // Prediabetes
          } else {
            // Non-fasting glucose levels
            if (glucose >= 200) riskScore += 20; // Diabetes
            else if (glucose >= 140) riskScore += 10; // Prediabetes
          }
        }
        
        // A1C Risk Factors (Hemoglobin A1C - diabetes indicator)
        if (latestBlood?.a1c) {
          const a1c = parseFloat(latestBlood.a1c);
          if (a1c >= 6.5) riskScore += 20; // Diabetes range
          else if (a1c >= 5.7) riskScore += 10; // Prediabetes range
          // Normal range (<5.7%) adds no risk points
        }
        
        // Lipoprotein(a) Risk Factors - Independent cardiovascular risk marker
        if (latestBlood?.lipoproteinA) {
          const lpa = parseInt(latestBlood.lipoproteinA);
          if (lpa >= 125) riskScore += 15; // High risk - significantly elevated cardiovascular risk
          else if (lpa >= 75) riskScore += 8; // Moderate risk - borderline high
          // Normal range (<75 nmol/L) adds no risk points
        }
        
        // Triglycerides Risk Factors
        if (latestBlood?.triglycerides) {
          const triglycerides = parseInt(latestBlood.triglycerides);
          if (triglycerides >= 500) riskScore += 20; // Very high - pancreatitis risk
          else if (triglycerides >= 200) riskScore += 12; // High
          else if (triglycerides >= 150) riskScore += 6; // Borderline high
          // Normal range (<150 mg/dL) adds no risk points
        }
        
        // EKG/ECG Risk Factors - Cardiac electrical abnormalities
        if (latestEkg?.heartRhythm) {
          const rhythm = String(latestEkg.heartRhythm).toLowerCase().replace(/_/g, ' ');
          if (rhythm.includes('atrial fibrillation') || rhythm.includes('afib')) riskScore += 25; // High stroke/heart failure risk
          else if (rhythm.includes('arrhythmia') || rhythm.includes('irregular')) riskScore += 15; // Moderate arrhythmia risk
          else if (rhythm.includes('bradycardia') || rhythm.includes('tachycardia')) riskScore += 8; // Rate abnormalities
          // Normal sinus rhythm adds no risk points
        }
        
        // QT Interval Risk Factors - Risk for dangerous arrhythmias
        if (latestEkg?.qtcInterval) {
          const qtc = parseInt(latestEkg.qtcInterval);
          if (qtc >= 500) riskScore += 20; // High risk for torsades de pointes
          else if (qtc >= 450) riskScore += 10; // Prolonged QTc - moderate risk
          else if (qtc <= 350) riskScore += 8; // Short QTc - arrhythmia risk
          // Normal range (350-450 ms) adds no risk points
        }
        
        // EKG Interpretation Risk Factors
        if (typeof latestEkg?.interpretation === 'string') {
          const interpretation = latestEkg.interpretation.toLowerCase();
          if (interpretation.includes('abnormal') || interpretation.includes('pathological')) riskScore += 15;
          else if (interpretation.includes('borderline') || interpretation.includes('nonspecific changes')) riskScore += 8;
          // Normal interpretation adds no risk points
        }
        
        // Heart Rate Risk Factors
        if (latestVital?.heartRate) {
          const hr = parseInt(latestVital.heartRate);
          if (hr > 100) riskScore += 5; // Tachycardia
          else if (hr < 50) riskScore += 5; // Bradycardia
        }
        
        // Severe findings alone floor the score at the high band (mirrors backend)
        const severeBp = latestVital?.systolicBP && latestVital?.diastolicBP &&
          (parseInt(latestVital.systolicBP) >= 180 || parseInt(latestVital.diastolicBP) >= 110);
        const severeRhythm = /atrial fibrillation|afib/.test(String(latestEkg?.heartRhythm || '').toLowerCase().replace(/_/g, ' '));
        const severeQtc = parseInt(latestEkg?.qtcInterval) >= 500;
        if ((severeBp || severeRhythm || severeQtc) && riskScore < 50) riskScore = 50;
        
        // Risk Level Classification - Updated thresholds to account for new risk factors
        if (!hasAnyTestData) riskLevel = 'unknown';      // No medical testing data to score
        else if (riskScore >= 80) riskLevel = 'critical';     // Very high risk - multiple severe factors
        else if (riskScore >= 50) riskLevel = 'high';    // High risk - significant risk factors
        else if (riskScore >= 25) riskLevel = 'medium';  // Medium risk - moderate risk factors
        else if (riskScore >= 8) riskLevel = 'low';      // Low risk - minimal risk factors
        else riskLevel = 'minimal';                      // Minimal risk - very few or no risk factors
        
        // Normalize score to 0-100 scale for display (max possible score ~300)
        let normalizedScore = Math.min(100, Math.round((riskScore / 200) * 100));

        // Use stored risk score from database if available (takes priority over calculated)
        if (riskVisit?.normalizedRiskScore !== undefined && riskVisit?.normalizedRiskScore !== null) {
          normalizedScore = riskVisit.normalizedRiskScore;
          riskScore = riskVisit.riskScore || riskScore;
          riskLevel = riskVisit.riskLevel || riskLevel;
        }

        // Prefer the backend's denormalized risk band (the field the server-side
        // risk filter queries) so the displayed level matches the filter results.
        if (fullPatientData?.riskLevel) {
          riskLevel = fullPatientData.riskLevel;
          if (typeof fullPatientData.riskScore === 'number') riskScore = fullPatientData.riskScore;
          if (typeof fullPatientData.normalizedRiskScore === 'number') normalizedScore = fullPatientData.normalizedRiskScore;
        }

        // Per-visit body measurements extraction (latest visit with any measurements)
        let latestBodyMeasurements = null;
        try {
          if (visits.length) {
            const sortedAnthro = visits.slice().sort((a,b)=> new Date(b.visitDate)-new Date(a.visitDate));
            latestBodyMeasurements = sortedAnthro.find(v => v.bodyMeasurements && (
              v.bodyMeasurements.heightInches || v.bodyMeasurements.weightLbs || v.bodyMeasurements.waistInches || v.bodyMeasurements.hipInches || v.bodyMeasurements.whr
            ))?.bodyMeasurements || null;
          }
        } catch(e) { /* silent */ }

        // Pick raw timestamps for stable sorting/filtering
        const checkInTimeRaw = patient.checkInTime || null;
        const completionTimeRaw = patient.screeningCompleteTime || patient.completionTime || patient.updatedAt || null;

        return {
          id: patient._id,
          patientId: patientData.patientId,
          patientName: `${patientData.firstName || ''} ${patientData.lastName || ''}`.trim() || 'Unknown',
          testingCode: fullPatientData?.testingCode || '',
          age: age,
          gender: patientData.birthGender || patientData.gender || 'Not specified',
          email: patientData.emailAddress || 'Not provided',
          phone: patientData.phoneNumber || 'Not provided',
          
          // Vital signs from screening data
          bloodPressure: latestVital?.systolicBP && latestVital?.diastolicBP 
            ? `${latestVital.systolicBP}/${latestVital.diastolicBP}` 
            : 'Not recorded',
          bpReadingsHistory: (latestVisit?.vitalSigns || [])
            .slice()
            .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
            .filter((v) => v?.systolicBP && v?.diastolicBP)
            .map((v) => ({
              bp: `${v.systolicBP}/${v.diastolicBP}`,
              heartRate: v.heartRate || '',
              timeLabel: formatHistoryTimestamp(v.timestamp),
              timestamp: v.timestamp,
            })),
          bpReadingsRaw: (latestVisit?.vitalSigns || [])
            .slice()
            .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0))
            .filter((v) => v?.systolicBP && v?.diastolicBP)
            .map((v, idx) => ({
              id: v.id || v._id || `reading_${idx}`,
              systolicBP: v.systolicBP,
              diastolicBP: v.diastolicBP,
              heartRate: v.heartRate || null,
              timestamp: v.timestamp || null,
              readingNumber: v.readingNumber || idx + 1,
              position: v.position || 'sitting'
            })),
          heartRate: latestVital?.heartRate || 'Not recorded',
          temperature: screeningData.temperature || 'Not recorded',
          height: latestBodyMeasurements?.heightInches ? `${latestBodyMeasurements.heightInches} inches` : (screeningData.height ? `${screeningData.height} inches` : 'Not recorded'),
          weight: latestBodyMeasurements?.weightLbs ? `${latestBodyMeasurements.weightLbs} lbs` : (screeningData.weight ? `${screeningData.weight} lbs` : 'Not recorded'),
          waistCircumference: latestBodyMeasurements?.waistInches ? `${latestBodyMeasurements.waistInches} inches` : (screeningData.waistCircumference ? `${screeningData.waistCircumference} inches` : 'Not recorded'),
          hipCircumference: latestBodyMeasurements?.hipInches ? `${latestBodyMeasurements.hipInches} inches` : (screeningData.hipCircumference ? `${screeningData.hipCircumference} inches` : 'Not recorded'),
          whr: latestBodyMeasurements?.whr || screeningData.whr || 'Not calculated',
          
          // Blood test results
          totalCholesterol: latestBlood?.totalCholesterol || 'Not tested',
          hdlCholesterol: latestBlood?.hdlCholesterol || 'Not tested',
          ldlCholesterol: latestBlood?.ldlCholesterol || 'Not tested',
          triglycerides: latestBlood?.triglycerides || 'Not tested',
          bloodGlucose: latestBlood?.bloodGlucose || 'Not tested',
            a1c: latestBlood?.a1c || 'Not tested',
          lipoproteinA: latestBlood?.lipoproteinA || 'Not tested',
          eGFR: getSpreadsheetEgfr(screeningData) ?? 'Not tested',
          fastingStatus: latestBlood?.fastingStatus ? 'Fasting' : 'Non-fasting',
          
          // EKG/ECG results
          heartRhythm: latestEkg?.heartRhythm || 'Not performed',
          qrsDuration: latestEkg?.qrsDuration || 'Not measured',
          prInterval: latestEkg?.prInterval || 'Not measured',
          qtInterval: latestEkg?.qtInterval || 'Not measured',
          qtcInterval: latestEkg?.qtcInterval || 'Not measured',
          ekgInterpretation: latestEkg?.interpretation || 'Not provided',
          
          // Risk assessment
          riskLevel: riskLevel,
          riskScore: normalizedScore, // Normalized to 0-100 scale
          rawRiskScore: riskScore, // Keep raw score for reference
          
          // Event and location information
          location: patient.eventInfo?.eventLocation || patient.eventId?.location?.name || 'Unknown Location',
          eventTitle: patient.eventInfo?.eventTitle || patient.eventId?.title || 'Unknown Event',
          
          // Screening details (formatted for display)
          status: patient.status,
          checkInDate: checkInTimeRaw ? new Date(checkInTimeRaw).toLocaleDateString() : 'Unknown',
          completionDate: completionTimeRaw
            ? new Date(completionTimeRaw).toLocaleDateString()
            : 'Unknown',

          // Raw timestamps for logic (sorting/filtering)
          checkInTimeRaw,
          completionTimeRaw,
          
          // Medical history flags
          medicalConditions: {
            diabetes: medicalHistory.diabetes || false,
            hypertension: medicalHistory.hypertension || false,
            heartDisease: medicalHistory.priorHeartAttack || false,
            highCholesterol: medicalHistory.highCholesterol || false,
            familyHistory: medicalHistory.familyHistoryHeartDisease || false
          },
          
          // Current medications
          currentMedications: medications,
          
          // Clinical notes and recommendations
          clinicalNotes: latestVisit?.clinicalNotes || fullPatientData?.notes || 'No notes recorded',
          recommendations: latestVisit?.recommendations || 'No specific recommendations recorded',
          followUpRequired: latestVisit?.followUpRequired || false,

          // Blood test handler
          bloodTestHandler: fullPatientData?.bloodTestHandler || '',
          bloodTestHandlerName: fullPatientData?.bloodTestHandlerName || '',

          kitStatus: kitStatusFromPatient(fullPatientData),
          
          // Complete patient data for detailed view
          fullPatientData: fullPatientData
        };
      }));

      // Filter out any null records and sort by raw timestamps (newest first)
      const validRecords = screeningRecords
        .filter(record => record !== null)
        .sort((a, b) => {
          const bTime = b.completionTimeRaw || b.checkInTimeRaw || 0;
          const aTime = a.completionTimeRaw || a.checkInTimeRaw || 0;
          return new Date(bTime) - new Date(aTime);
        });
      
      setScreenings(validRecords);
      
    } catch (error) {
      console.error('Error loading screening data:', error);
      message.error('Failed to load screening data');
    } finally {
      setLoading(false);
    }
  };

  // Row click navigates to member record; inline details modal trigger removed with Actions column

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys, rows) => {
      setSelectedRowKeys(keys);
      // Merge the newly reported rows into our cache, then keep only the records
      // that are still selected. This retains records for selected members that
      // have scrolled out of the current search/filter view.
      setSelectedRecordsMap((prev) => {
        const merged = { ...prev };
        (rows || []).forEach((r) => {
          if (r && r.id != null) merged[r.id] = r;
        });
        const next = {};
        keys.forEach((k) => {
          if (merged[k]) next[k] = merged[k];
        });
        return next;
      });
      setAllMatchingSelected(false);
      setAllMatchingPatientIds([]);
    },
    preserveSelectedRowKeys: true,
  };

  const handleExportData = () => {
    if (filteredScreenings.length === 0) {
      message.warning('No data to export');
      return;
    }
    setIsBulkExportOpen(true);
  };

  const handleAddToCallQueue = async () => {
    const ids = [...new Set(allMatchingSelected
      ? allMatchingPatientIds
      : selectedRecords.map(r => r.patientId).filter(Boolean))];
    if (ids.length === 0) {
      message.warning('No members selected');
      return;
    }
    if (ids.length > 100) {
      message.warning('Add at most 100 members to Case Management at a time.');
      return;
    }
    const submit = async (allowMissingConsent) => {
      const res = await caseService.addToCallQueue(ids, { allowMissingConsent });
      if (res?.code === 'MISSING_CALL_CONSENT') return res;
      if (!res?.success) throw new Error(res?.error || 'Failed to add to Case Management');
      const { added, alreadyQueued } = res.data;
      message.success(
        `${added} added to Case Management${alreadyQueued ? ` (${alreadyQueued} already there)` : ''}`
      );
      return res;
    };
    try {
      const res = await submit(false);
      if (res?.code !== 'MISSING_CALL_CONSENT') return;
      const missing = res.data?.missing || [];
      const shown = missing.slice(0, 10);
      Modal.confirm({
        title: missing.length === 1
          ? 'AI call consent wasn\'t checked'
          : `${missing.length} members didn't check AI call consent`,
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>These members didn&apos;t check the AI voice call consent on their intake form:</p>
            <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
              {shown.map((m) => <li key={m.patientId}>{m.name}</li>)}
            </ul>
            {missing.length > shown.length && (
              <p style={{ color: '#8c8c8c' }}>and {missing.length - shown.length} more</p>
            )}
            <p>You can still add them to Case Management.</p>
          </div>
        ),
        okText: 'Add anyway',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await submit(true);
          } catch (err) {
            message.error(err.message || 'Failed to add to Case Management');
          }
        }
      });
    } catch (err) {
      message.error(err.message || 'Failed to add to Case Management');
    }
  };

  const handleBulkArchive = () => {
    const selectedPatientIds = allMatchingSelected
      ? allMatchingPatientIds
      : selectedRecords.map(r => r.patientId).filter(Boolean);
    if (selectedPatientIds.length === 0) {
      message.warning('No patients selected');
      return;
    }
    Modal.confirm({
      title: `Archive ${selectedPatientIds.length} selected member${selectedPatientIds.length > 1 ? 's' : ''}?`,
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>This will archive the selected members. Their records will be moved to the Archived status and hidden from active views, but all data is retained per HIPAA requirements.</p>
          <p style={{ color: '#8c8c8c', marginTop: 12 }}>Archived records can be viewed by filtering for the &quot;Archived&quot; status.</p>
        </div>
      ),
      okText: `Archive ${selectedPatientIds.length} Member${selectedPatientIds.length > 1 ? 's' : ''}`,
      cancelText: 'Cancel',
      onOk: async () => {
        setBulkArchiving(true);
        try {
          const result = await DataImportService.bulkArchive(selectedPatientIds);
          if (result.success) {
            const a = result.archived;
            message.success(
              `Archived ${a.patients} patients and ${a.visits} visits`
            );
            setSelectedRowKeys([]);
            setSelectedRecordsMap({});
            setAllMatchingSelected(false);
            setAllMatchingPatientIds([]);
            loadScreenings(1, pagination.pageSize);
          }
        } catch (err) {
          message.error(err.message || 'Failed to archive selected members');
        } finally {
          setBulkArchiving(false);
        }
      },
    });
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignHandler) {
      message.warning('Please select a team member');
      return;
    }
    if (selectedRowKeys.length === 0) {
      message.warning('No members selected');
      return;
    }
    setBulkAssignLoading(true);
    try {
      const patientIds = selectedRecords.map(r => r.patientId);
      const results = await Promise.allSettled(
        patientIds.map(pid => PatientService.updatePatient(pid, { bloodTestHandler: bulkAssignHandler }))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) {
        message.warning(`Updated ${succeeded} members, ${failed} failed`);
      } else {
        message.success(`Updated ${succeeded} members`);
      }
      setIsBulkAssignOpen(false);
      setBulkAssignHandler(null);
      setSelectedRowKeys([]);
      setSelectedRecordsMap({});
      // Refresh the list
      loadScreenings(pagination.current, pagination.pageSize);
    } catch (err) {
      console.error('Bulk assign error:', err);
      message.error('Failed to update members');
    } finally {
      setBulkAssignLoading(false);
    }
  };

  const handleSelectAllMatching = async () => {
    try {
      const response = await PatientService.getAllMatchingIds(currentExportFilters);
      const ids = response.ids || [];
      setAllMatchingPatientIds(ids);
      setAllMatchingSelected(true);
      message.success(`Selected all ${ids.length} matching records`);
    } catch (err) {
      message.error('Failed to select all records');
    }
  };

  const handleClearSelectAll = () => {
    setSelectedRowKeys([]);
    setSelectedRecordsMap({});
    setAllMatchingSelected(false);
    setAllMatchingPatientIds([]);
  };

  const getRiskColor = (riskLevel) => {
    const colors = {
      minimal: '#87d068',  // Light green for minimal risk
      low: '#52c41a',      // Green for low risk
      medium: '#faad14',   // Yellow/orange for medium risk
      high: '#f5222d',     // Red for high risk
      critical: '#820014'  // Dark red for critical risk
    };
    return colors[riskLevel] || '#d9d9d9';
  };

  const getRiskLabel = (riskLevel) => (
    !riskLevel || riskLevel === 'unknown' ? 'NO DATA' : String(riskLevel).toUpperCase()
  );

  // Risk breakdown calculator removed

  // Risk tag click previously opened a breakdown modal; removed for this page

  // Risk filtering is applied server-side via the denormalized Patient.riskLevel.
  const filteredScreenings = screenings;

  // Bulk selection helpers (must be after filteredScreenings)
  const selectedRecords = useMemo(() => {
    // Derive from the cache (keyed by id) so records for selected members that
    // are no longer in the current search/filter view are still included.
    // Fall back to the current data for any key missing from the cache.
    return selectedRowKeys
      .map((k) => selectedRecordsMap[k] || filteredScreenings.find((s) => s.id === k))
      .filter(Boolean);
  }, [filteredScreenings, selectedRowKeys, selectedRecordsMap]);

  // Build the filter params object that mirrors what loadScreenings sends to the backend.
  // This is passed to BulkExportModal so the "Export All" option can fetch every matching record.
  const currentExportFilters = useMemo(() => {
    const ALL_QUEUE_STATUSES = [
      'completed','in_progress','pending_review',
      'new_patient','matched','needs_attention','duplicate'
    ];
    let statusFilters = ALL_QUEUE_STATUSES;
    if (statusFilter === 'completed') statusFilters = ['completed'];
    else if (statusFilter === 'archived') statusFilters = ['archived'];
    else if (statusFilter === 'in_progress') statusFilters = ['in_progress'];
    else if (statusFilter === 'pending_review') statusFilters = ['pending_review'];

    const params = { tabFilter: statusFilters, sort: 'newest' };
    if (searchText && searchText.trim()) params.search = searchText.trim();
    if (locationFilter && locationFilter !== 'all') params.eventTitle = locationFilter;
    if (dateRange && dateRange.length === 2) {
      params.dateFrom = dateRange[0].toISOString();
      params.dateTo = dateRange[1].toISOString();
    }
    if (dobFilter && dayjs(dobFilter).isValid()) params.dob = dayjs(dobFilter).format('YYYY-MM-DD');
    if (dataSourceFilter && dataSourceFilter !== 'all') params.dataSource = dataSourceFilter;
    if (riskFilter && riskFilter !== 'all') params.riskLevel = riskFilter;
    return params;
  }, [statusFilter, searchText, locationFilter, dateRange, dobFilter, dataSourceFilter, riskFilter]);

  // Use server-provided event titles for filter dropdown (covers all pages, not just current)
  const locations = allEventTitles.length > 0
    ? allEventTitles
    : [...new Set(screenings.map(s => s.eventTitle))].filter(Boolean);

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setRiskFilter('all');
    setLocationFilter('all');
    setDateRange(null);
    setDobFilter(null);
    setDataSourceFilter('all');
    setIsFilterDrawerOpen(false);
  };

  const handleApplyFilters = () => {
    setIsFilterDrawerOpen(false);
  };

  const columns = [
    {
      title: 'Member',
      dataIndex: 'patientName',
      key: 'patientName',
      width: 160,
      render: (name, record) => (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <Text strong style={{ color: COLORS.textDark }}>{name}</Text>
          <KitStatusIcons status={record.kitStatus} />
        </span>
      ),
      sorter: (a, b) => a.patientName.localeCompare(b.patientName),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
      render: (phone) => (
        <Text style={{ fontSize: '12px' }}>
          <PhoneOutlined style={{ marginRight: 4, color: COLORS.$primaryBlue }} />
          {String(phone)}
        </Text>
      ),
    },
    {
      title: 'Age',
      dataIndex: 'age',
      key: 'age',
      width: 40,
      render: (age) => (
        <Text style={{ fontSize: '12px', fontWeight: '500' }}>
          {age || 'N/A'}
        </Text>
      ),
      sorter: (a, b) => (a.age || 0) - (b.age || 0),
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      width: 90,
      render: (gender) => (
        <Text style={{ fontSize: '12px' }}>
          {gender || 'N/A'}
        </Text>
      ),
    },
    {
      title: 'Event',
      dataIndex: 'eventTitle',
      key: 'event',
      width: 180,
      render: (eventTitle) => (
        <Text style={{ fontSize: '12px', fontWeight: '500' }}>
          {eventTitle || 'Unknown Event'}
        </Text>
      ),
    },
    {
      title: 'Risk',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 100,
      render: (riskLevel) => (
        <RiskTag 
          color={getRiskColor(riskLevel)}
        >
          {getRiskLabel(riskLevel)}
        </RiskTag>
      ),
      sorter: (a, b) => a.riskScore - b.riskScore,
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, record) => (
        <Space size={4}>
          <Badge 
            status={record.status === 'completed' ? 'success' : record.status === 'archived' ? 'default' : 'processing'} 
            text={<Text style={{ fontSize: '12px' }}>{String(record.status).replace('_', ' ')}</Text>}
          />
          {record.followUpRequired && (
            <Tag color="orange" style={{ fontSize: '10px', margin: 0 }}>
              Follow-up
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Sent By',
      dataIndex: 'bloodTestHandlerName',
      key: 'bloodTestHandler',
      width: 70,
      render: (name) => {
        if (!name) return <Text style={{ fontSize: '12px', color: '#999' }}>—</Text>;
        const parts = name.trim().split(/\s+/);
        const initials = parts.map(p => p.charAt(0).toUpperCase()).join('');
        return (
          <Tag style={{ fontSize: '11px', margin: 0 }}>
            {initials}
          </Tag>
        );
      },
    },
  ];

  const navigate = useNavigate();

  // Bulk-action menu, shared by the desktop dropdown and the mobile bottom sheet.
  const getActionItems = () => {
    const count = allMatchingSelected ? allMatchingPatientIds.length : selectedRowKeys.length;
    const pageCount = selectedRowKeys.length;
    const activatableCount = selectedRecords.filter((r) => !r.kitStatus).length;
    const icon = (fa) => <FontAwesomeIcon icon={fa} fixedWidth />;
    return [
      {
        key: 'add-member',
        icon: icon(faUserPlus),
        label: 'Add member',
        onClick: () => setIsAddMemberOpen(true),
      },
      {
        key: 'export',
        icon: icon(faDownload),
        label: count > 0 ? `Export selected (${count})` : 'Export all',
        onClick: handleExportData,
      },
      ...(count > 0 ? [
        { type: 'divider' },
        {
          key: 'email',
          icon: icon(faEnvelope),
          label: `Email selected (${pageCount})`,
          disabled: pageCount === 0,
          onClick: () => {
            if (selectedRecords.length === 0) {
              message.warning('Select members on this page to email.');
              return;
            }
            setIsEmailModalOpen(true);
          },
        },
        {
          key: 'assign',
          icon: icon(faUserPen),
          label: `Assign sent by (${count})`,
          onClick: () => setIsBulkAssignOpen(true),
        },
        {
          key: 'call-queue',
          icon: icon(faPhoneVolume),
          label: `Add to Case Management (${count})`,
          onClick: handleAddToCallQueue,
        },
        { type: 'divider' },
        {
          key: 'activate-kits',
          icon: icon(faVial),
          label: pageCount > 0 && activatableCount === 0
            ? 'Activate kits (already activated)'
            : `Activate kits (${activatableCount})`,
          disabled: activatableCount === 0,
          onClick: () => {
            if (selectedRecords.length === 0) {
              message.warning('Select members on this page to activate kits.');
              return;
            }
            setIsKitActivationOpen(true);
          },
        },
        {
          key: 'kit-results',
          icon: icon(faFileMedical),
          label: `Kit results (${pageCount})`,
          disabled: pageCount === 0,
          onClick: () => {
            if (selectedRecords.length === 0) {
              message.warning('Select members on this page to view kit results.');
              return;
            }
            setIsKitResultsOpen(true);
          },
        },
        { type: 'divider' },
        {
          key: 'archive',
          icon: icon(faBoxArchive),
          label: `Archive selected (${count})`,
          danger: true,
          disabled: bulkArchiving,
          onClick: handleBulkArchive,
        },
      ] : []),
    ];
  };

  return (
    <>
      <GlobalSelectStyles />
        <PageContainer>

        <div className="filters-animate-in">
          <FilterContainer style={{ marginBottom: '16px' }}>
            <Row gutter={[12, 12]} align="middle">
              <Col flex="auto">
                <StyledInput
                  placeholder="Search patients, emails, phones, or locations..."
                  prefix={<SearchOutlined style={{ color: COLORS.textMuted, fontSize: '16px' }} />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ 
                    width: '100%',
                    height: '40px',
                    borderRadius: '10px',
                    fontSize: '14px'
                  }}
                  allowClear
                />
              </Col>
              
              <Col>
                <Badge count={[statusFilter !== 'all', riskFilter !== 'all', locationFilter !== 'all', dateRange != null, dataSourceFilter !== 'all'].filter(Boolean).length} size="small" offset={[-4, 4]}>
                  <MobileFilterButton
                    icon={<FilterOutlined />}
                    onClick={() => setIsFilterDrawerOpen(true)}
                    style={{
                      height: '40px',
                      borderRadius: '10px',
                      fontSize: '14px'
                    }}
                  >
                    Filter
                  </MobileFilterButton>
                </Badge>
              </Col>
              
              <Col className="members-view-toggle">
                <div style={{ display: 'flex', borderRadius: '10px', overflow: 'hidden', border: '1px solid #d9d9d9', height: '40px' }}>
                  <button
                    onClick={() => setViewMode('list')}
                    style={{
                      padding: '0 14px',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: viewMode === 'list' ? '#000' : '#fff',
                      color: viewMode === 'list' ? '#fff' : '#666',
                      transition: 'all 0.2s',
                    }}
                  >
                    <FontAwesomeIcon icon={faList} /> List
                  </button>
                  <button
                    onClick={() => setViewMode('spreadsheet')}
                    style={{
                      padding: '0 14px',
                      border: 'none',
                      borderLeft: '1px solid #d9d9d9',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: viewMode === 'spreadsheet' ? '#000' : '#fff',
                      color: viewMode === 'spreadsheet' ? '#fff' : '#666',
                      transition: 'all 0.2s',
                    }}
                  >
                    <FontAwesomeIcon icon={faTableCells} /> Spreadsheet
                  </button>
                </div>
              </Col>
              <Col className="members-actions-desktop">
                <Dropdown
                  menu={{ items: getActionItems() }}
                  trigger={['click']}
                  placement="bottomRight"
                  overlayClassName="members-actions-menu"
                >
                  <ActionButton
                    style={{
                      height: '40px',
                      borderRadius: '10px',
                      fontSize: '14px',
                      backgroundColor: '#000',
                      borderColor: '#000',
                      color: '#fff'
                    }}
                  >
                    Actions{(allMatchingSelected ? allMatchingPatientIds.length : selectedRowKeys.length) > 0 ? ` (${allMatchingSelected ? allMatchingPatientIds.length : selectedRowKeys.length})` : ''} <DownOutlined style={{ fontSize: 11 }} />
                  </ActionButton>
                </Dropdown>
              </Col>

              <Col className="members-actions-mobile">
                <Badge
                  count={allMatchingSelected ? allMatchingPatientIds.length : selectedRowKeys.length}
                  size="small"
                  offset={[-4, 4]}
                >
                  <MobileIconButton
                    icon={<MoreOutlined style={{ fontSize: 18 }} />}
                    aria-label="Actions"
                    onClick={() => setIsActionsSheetOpen(true)}
                  />
                </Badge>
              </Col>
            </Row>
          </FilterContainer>
        </div>

        {/* Active Filter Tags */}
        {(statusFilter !== 'all' || riskFilter !== 'all' || locationFilter !== 'all' || dateRange != null || dobFilter != null || dataSourceFilter !== 'all') && (
          <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>Active filters:</Text>
            {statusFilter !== 'all' && (
              <Tag closable onClose={() => setStatusFilter('all')} color="blue">
                Status: {statusFilter.replace('_', ' ')}
              </Tag>
            )}
            {riskFilter !== 'all' && (
              <Tag closable onClose={() => setRiskFilter('all')} color="orange">
                Risk: {riskFilter}
              </Tag>
            )}
            {locationFilter !== 'all' && (
              <Tag closable onClose={() => setLocationFilter('all')} color="green">
                Event: {locationFilter}
              </Tag>
            )}
            {dataSourceFilter !== 'all' && (
              <Tag closable onClose={() => setDataSourceFilter('all')} color="purple">
                Source: {dataSourceFilter === 'import' ? 'Imported' : 'System'}
              </Tag>
            )}
            {dateRange != null && (
              <Tag closable onClose={() => setDateRange(null)} color="cyan">
                Date range
              </Tag>
            )}
            {dobFilter != null && (
              <Tag closable onClose={() => setDobFilter(null)} color="geekblue">
                DOB: {dayjs(dobFilter).format('MM/DD/YYYY')}
              </Tag>
            )}
            <Tag
              style={{ cursor: 'pointer', borderStyle: 'dashed' }}
              onClick={() => { setStatusFilter('all'); setRiskFilter('all'); setLocationFilter('all'); setDataSourceFilter('all'); setDateRange(null); setDobFilter(null); }}
            >
              Clear all
            </Tag>
            <Text type="secondary" style={{ fontSize: 13, marginLeft: 'auto' }}>
              {pagination.total} record{pagination.total !== 1 ? 's' : ''} found
            </Text>
          </div>
        )}

        {/* Patient Statistics Summary */}
        {patientStats && (
          <Row gutter={16} style={{ marginBottom: 16 }} className="filters-animate-in">
            <Col span={24}>
              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                border: `1px solid ${COLORS.border}`
              }}>
                <Space size={24} wrap>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', color: COLORS.$primaryBlue }}>
                      {patientStats.total || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: COLORS.textMuted }}>Total Records</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}>
                      {patientStats.completed || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: COLORS.textMuted }}>Completed</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}>
                      {patientStats.in_progress || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: COLORS.textMuted }}>In Progress</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#faad14' }}>
                      {patientStats.pending_review || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: COLORS.textMuted }}>Pending Review</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#8c8c8c' }}>
                      {patientStats.archived || 0}
                    </div>
                    <div style={{ fontSize: '12px', color: COLORS.textMuted }}>Archived</div>
                  </div>
                </Space>
              </div>
            </Col>
          </Row>
        )}

        <DesktopMembersView>
        {viewMode === 'list' ? (
          <StyledTableContainer className="table-animate-in" style={{ position: 'relative' }}>
            {/* Select All Across Pages Banner */}
            {selectedRowKeys.length > 0 && selectedRowKeys.length >= filteredScreenings.length && pagination.total > filteredScreenings.length && !allMatchingSelected && (
              <div style={{
                background: '#e6f4ff',
                borderBottom: '1px solid #91caff',
                padding: '8px 16px',
                textAlign: 'center',
                fontSize: 13,
              }}>
                All {filteredScreenings.length} records on this page are selected.{' '}
                <a onClick={handleSelectAllMatching} style={{ fontWeight: 600 }}>
                  Select all {pagination.total} records matching current filters
                </a>
              </div>
            )}
            {allMatchingSelected && (
              <div style={{
                background: '#f6ffed',
                borderBottom: '1px solid #b7eb8f',
                padding: '8px 16px',
                textAlign: 'center',
                fontSize: 13,
              }}>
                All {allMatchingPatientIds.length} records matching current filters are selected.{' '}
                <a onClick={handleClearSelectAll} style={{ fontWeight: 600 }}>
                  Clear selection
                </a>
              </div>
            )}
            {loading && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                minHeight: '200px'
              }}>
                <HeartLoader size={48} />
              </div>
            )}
            <StyledTable
              rowSelection={rowSelection}
              columns={columns}
              dataSource={filteredScreenings}
              rowKey="id"
              onRow={(record) => ({
                onClick: (e) => {
                  // Don't navigate if clicking on checkbox
                  if (e.target.closest('.ant-checkbox-wrapper') || e.target.closest('.ant-checkbox')) return;
                  navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', record.patientId));
                },
                style: { cursor: 'pointer' }
              })}
              scroll={{ x: 'max-content', y: 1 }}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} screenings`,
                onChange: (page, pageSize) => {
                  setPagination(prev => ({ ...prev, current: page, pageSize }));
                  loadScreenings(page, pageSize);
                },
                onShowSizeChange: (current, size) => {
                  setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                  loadScreenings(1, size);
                }
              }}
            />
          </StyledTableContainer>
        ) : (
          <div className="table-animate-in" style={{ flex: 1, minHeight: 0, position: 'relative' }}>
            <SpreadsheetView
              data={filteredScreenings}
              loading={loading}
              teamMembers={teamMembers}
              onRefresh={() => loadScreenings(pagination.current, pagination.pageSize)}
              pagination={pagination}
            />
          </div>
        )}
        </DesktopMembersView>

        <MobileMembersList>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <HeartLoader size={40} />
            </div>
          ) : filteredScreenings.length === 0 ? (
            <div style={{ textAlign: 'center', color: COLORS.textMuted, padding: '32px 0' }}>
              No members found
            </div>
          ) : (
            filteredScreenings.map((record) => {
              const handler = (record.bloodTestHandlerName || '').trim();
              const initials = handler
                ? handler.split(/\s+/).map((p) => p.charAt(0).toUpperCase()).join('')
                : '';
              const risk = String(record.riskLevel || 'unknown');
              return (
                <MemberCard
                  key={record.id}
                  onClick={() => navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', record.patientId))}
                >
                  <div className="mc-head">
                    <div className="mc-title">
                      {record.patientName}{initials ? ` (${initials})` : ''}
                      <KitStatusIcons status={record.kitStatus} />
                    </div>
                    <RiskTag color={getRiskColor(risk)}>{getRiskLabel(risk)}</RiskTag>
                  </div>
                  <div className="mc-contact">
                    <span><PhoneOutlined />{String(record.phone)}</span>
                    <span className="mc-demo">{record.age || 'N/A'} · {record.gender || 'N/A'}</span>
                  </div>
                  {record.eventTitle && (
                    <div className="mc-event">{record.eventTitle}</div>
                  )}
                  <div className="mc-status">
                    <Badge
                      status={record.status === 'completed' ? 'success' : record.status === 'archived' ? 'default' : 'processing'}
                      text={<Text style={{ fontSize: '13px' }}>{String(record.status).replace('_', ' ')}</Text>}
                    />
                    {record.followUpRequired && (
                      <Tag color="orange" style={{ fontSize: '10px', margin: 0 }}>Follow-up</Tag>
                    )}
                  </div>
                </MemberCard>
              );
            })
          )}
        </MobileMembersList>

        {pagination.total > 0 && (() => {
          const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.pageSize));
          const goTo = (page) => {
            setPagination(prev => ({ ...prev, current: page }));
            loadScreenings(page, pagination.pageSize);
          };
          return (
            <MobileMembersPagination>
              <button
                type="button"
                className="pg-btn"
                disabled={pagination.current <= 1}
                onClick={() => goTo(pagination.current - 1)}
              >
                <LeftOutlined /> Prev
              </button>
              <span className="pg-info">
                Page <strong>{pagination.current}</strong> of {totalPages}
              </span>
              <button
                type="button"
                className="pg-btn"
                disabled={pagination.current >= totalPages}
                onClick={() => goTo(pagination.current + 1)}
              >
                Next <RightOutlined />
              </button>
            </MobileMembersPagination>
          );
        })()}

        {/* Bulk Export Modal */}
        <BulkExportModal
          visible={isBulkExportOpen}
          onClose={() => setIsBulkExportOpen(false)}
          selectedRecords={selectedRowKeys.length > 0 ? selectedRecords : filteredScreenings}
          currentFilters={currentExportFilters}
          totalRecords={pagination.total}
        />

        {/* Bulk Kit Activation Modal */}
        <BulkKitActivationModal
          visible={isKitActivationOpen}
          onClose={() => {
            setIsKitActivationOpen(false);
            loadScreenings(pagination.current, pagination.pageSize);
          }}
          patientIds={selectedRecords.filter((r) => !r.kitStatus).map((r) => r.patientId).filter(Boolean)}
        />

        {/* Bulk Kit Results Modal */}
        <BulkKitResultsModal
          visible={isKitResultsOpen}
          onClose={() => setIsKitResultsOpen(false)}
          patientIds={selectedRecords.map((r) => r.patientId).filter(Boolean)}
        />

        <AddMemberModal
          open={isAddMemberOpen}
          onClose={() => setIsAddMemberOpen(false)}
          onCreated={(patientId, { openRecord, existing }) => {
            setIsAddMemberOpen(false);
            if (openRecord) {
              navigate(ROUTES.ADMIN_MEMBER_RECORD_EDIT.replace(':patientId', patientId));
            } else if (!existing) {
              loadScreenings(1, pagination.pageSize);
            }
          }}
        />

        {/* Email Outreach Modal */}
        <ComposeOutreachModal
          visible={isEmailModalOpen}
          method="email"
          recipients={selectedRecords.map((r) => ({
            patientId: r.patientId,
            name: r.patientName,
            email: r.email && r.email !== 'Not provided' ? r.email : null,
            phone: r.phone && r.phone !== 'Not provided' ? r.phone : null
          }))}
          onClose={() => setIsEmailModalOpen(false)}
          onSent={() => setIsEmailModalOpen(false)}
        />

        {/* Bulk Assign Handler Modal */}
        <Modal
          title={`Assign Blood Test Handler (${selectedRowKeys.length} members)`}
          open={isBulkAssignOpen}
          onOk={handleBulkAssign}
          onCancel={() => { setIsBulkAssignOpen(false); setBulkAssignHandler(null); }}
          confirmLoading={bulkAssignLoading}
          okText="Assign"
        >
          <div style={{ marginBottom: 8 }}>Select the team member who sent off the blood tests:</div>
          <Select
            style={{ width: '100%' }}
            placeholder="Select team member"
            value={bulkAssignHandler}
            onChange={setBulkAssignHandler}
            showSearch
            optionFilterProp="children"
            allowClear
          >
            {teamMembers.map(member => (
              <Select.Option key={member._id} value={member._id}>
                {member.fullName || member.email}
              </Select.Option>
            ))}
          </Select>
        </Modal>

        {/* Mobile Actions bottom sheet */}
        <Drawer
          title="Actions"
          placement="bottom"
          height="auto"
          open={isActionsSheetOpen}
          onClose={() => setIsActionsSheetOpen(false)}
          styles={{ content: { borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
        >
          <ActionsSheetList>
            {getActionItems().map((item, idx) =>
              item.type === 'divider' ? (
                <div className="divider" key={`divider-${idx}`} />
              ) : (
                <button
                  key={item.key}
                  type="button"
                  className={item.danger ? 'danger' : undefined}
                  disabled={item.disabled}
                  onClick={() => {
                    setIsActionsSheetOpen(false);
                    item.onClick?.();
                  }}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </button>
              )
            )}
          </ActionsSheetList>
        </Drawer>

        {/* Mobile Filter Drawer */}
        <FilterDrawer
          visible={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          riskFilter={riskFilter}
          setRiskFilter={setRiskFilter}
          locationFilter={locationFilter}
          setLocationFilter={setLocationFilter}
          dateRange={dateRange}
          setDateRange={setDateRange}
          dobFilter={dobFilter}
          setDobFilter={setDobFilter}
          dataSourceFilter={dataSourceFilter}
          setDataSourceFilter={setDataSourceFilter}
          locations={locations}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </PageContainer>
    </>
  );
};

export default ScreeningData;
